import _slicedToArray from "/home/greenhack/tfjs-yolov5-example/node_modules/@babel/runtime/helpers/esm/slicedToArray";

/**
 * @license
 * Copyright 2020 Google LLC. All Rights Reserved.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 * =============================================================================
 */
import { env, TopK, util } from '@tensorflow/tfjs-core';
import { topKImplCPU } from '../kernel_utils/shared';
import { MergeProgram, SwapProgram } from '../top_k_gpu';
import { fill } from './Fill';
import { gatherV2 } from './GatherV2';
import { reshape } from './Reshape';
import { slice } from './Slice';

function disposeIntermediateTensorInfoOrNull(backend, tensorInfo) {
  if (tensorInfo !== null) {
    backend.disposeIntermediateTensorInfo(tensorInfo);
  }
}

function roundUpToPow2(num) {
  var pow2 = 1;

  while (pow2 < num) {
    pow2 *= 2;
  }

  return pow2;
} // Based on Algorithm 2 of Bitonic Top K, ref:
// https://anilshanbhag.in/static/papers/gputopk_sigmod18.pdf


export function topK(args) {
  var inputs = args.inputs,
      backend = args.backend,
      attrs = args.attrs;
  var x = inputs.x;
  var k = attrs.k,
      sorted = attrs.sorted; // Empirically determined constant used to determine last dim threshold for
  // handing off execution to the CPU.

  var TOPK_LAST_DIM_CPU_HANDOFF_SIZE_THRESHOLD = env().getNumber('TOPK_LAST_DIM_CPU_HANDOFF_SIZE_THRESHOLD'); // Empirically determined constant used to determine k threshold for handing
  // off execution to the CPU.

  var TOPK_K_CPU_HANDOFF_THRESHOLD = env().getNumber('TOPK_K_CPU_HANDOFF_THRESHOLD');
  var xShape = x.shape;
  var lastDim = xShape[xShape.length - 1];

  if (backend.shouldExecuteOnCPU([x]) || lastDim < TOPK_LAST_DIM_CPU_HANDOFF_SIZE_THRESHOLD || k > TOPK_K_CPU_HANDOFF_THRESHOLD) {
    var xVals = backend.readSync(x.dataId);

    var _topKImplCPU = topKImplCPU(xVals, xShape, x.dtype, k, sorted),
        _topKImplCPU2 = _slicedToArray(_topKImplCPU, 2),
        allTopKVals = _topKImplCPU2[0],
        allTopKIndices = _topKImplCPU2[1];

    return [backend.makeTensorInfo(allTopKVals.shape, allTopKVals.dtype, allTopKVals.values), backend.makeTensorInfo(allTopKIndices.shape, allTopKIndices.dtype, allTopKIndices.values)];
  }

  if (k === 0) {
    xShape[xShape.length - 1] = 0;
    return [backend.makeTensorInfo(xShape, x.dtype, []), backend.makeTensorInfo(xShape, 'int32', [])];
  }

  if (lastDim === 1
  /* firstPass */
  ) {
    return [x, fill({
      attrs: {
        shape: xShape,
        dtype: 'int32',
        value: 0
      },
      backend: backend
    })];
  } // Eagerly unpack x input since it is passed in to all the shaders which
  // require unpacked inputs.


  var xtexData = backend.texData.get(x.dataId);
  var xIsPacked = xtexData !== null && xtexData.isPacked;
  var xUnPacked = xIsPacked ? backend.unpackTensor(x) : x; // Reshape into a 2d tensor [batch, lastDim] and compute topk along lastDim.

  var xSize = util.sizeFromShape(xShape);
  var batch = xSize / lastDim;
  var x2D = reshape({
    inputs: {
      x: xUnPacked
    },
    attrs: {
      shape: [batch, lastDim]
    },
    backend: backend
  });

  if (xIsPacked) {
    disposeIntermediateTensorInfoOrNull(backend, xUnPacked);
  }

  var kPow2 = roundUpToPow2(k);
  var lastDimPow2 = roundUpToPow2(lastDim); // Only the indices containing the top K are kept at every step to reduce
  // number of outputs in the GPU algorithms, so once the final set of indices
  // is computed then gather is used to grab the corresponding values
  // from the original input.

  var indices = null; // GPU algorithm always takes in an indices input but this input is not used
  // on the first run of a GPU algorithm, therefore if indices is null we simply
  // pass in x2D instead of it but the value will not actually be used

  var getInputs = function getInputs() {
    return indices === null ? [x2D, x2D] : [x2D, indices];
  };

  var runSwap = function runSwap(dir, inc, shape) {
    var inputs = getInputs();
    var program = new SwapProgram(shape);
    var fistPass = indices === null ? 1 : 0;
    var customValues = [[lastDim], [fistPass], [Number.NEGATIVE_INFINITY], [dir], [inc]];
    var prevIndices = indices;
    indices = backend.runWebGLProgram(program, inputs, 'int32', customValues);
    disposeIntermediateTensorInfoOrNull(backend, prevIndices);
  }; // Step 1: local sort


  for (var len = 1; len < kPow2; len *= 2) {
    var dir = len * 2;

    for (var inc = len; inc >= 1; inc /= 2) {
      runSwap(dir, inc, [batch, lastDimPow2]);
    }
  } // Step 2: merge


  for (var indicesSize = lastDimPow2; indicesSize > kPow2; indicesSize /= 2) {
    var _inputs = getInputs();

    var mergeProgram = new MergeProgram([batch, indicesSize / 2]);
    var firstPass = indices === null ? 1 : 0;
    var customValues = [[lastDim], [firstPass], [kPow2]];
    var _prevIndices = indices;
    indices = backend.runWebGLProgram(mergeProgram, _inputs, 'int32', customValues);
    disposeIntermediateTensorInfoOrNull(backend, _prevIndices); // Step 3: rebuild

    var _len = kPow2 / 2;

    var _dir = _len * 2;

    for (var _inc = _len; _inc >= 1; _inc /= 2) {
      runSwap(_dir, _inc, indices.shape);
    }
  } // Keep only the requested top K results instead of kPow2


  var prevIndices = indices;
  indices = slice({
    inputs: {
      x: indices
    },
    backend: backend,
    attrs: {
      begin: 0,
      size: [batch, k]
    }
  });
  disposeIntermediateTensorInfoOrNull(backend, prevIndices); // Gather values on last dimension

  var values = gatherV2({
    inputs: {
      x: x2D,
      indices: indices
    },
    backend: backend,
    attrs: {
      axis: 1,
      batchDims: 1
    }
  });
  disposeIntermediateTensorInfoOrNull(backend, x2D); // Reshape back to the original input shape, except that the last
  // dimension is k.

  var newShape = xShape.slice(0, -1);
  newShape.push(k);
  prevIndices = indices;
  indices = reshape({
    inputs: {
      x: indices
    },
    attrs: {
      shape: newShape
    },
    backend: backend
  });
  disposeIntermediateTensorInfoOrNull(backend, prevIndices);
  var prevValues = values;
  values = reshape({
    inputs: {
      x: values
    },
    attrs: {
      shape: newShape
    },
    backend: backend
  });
  disposeIntermediateTensorInfoOrNull(backend, prevValues);
  return [values, indices];
}
export var topKConfig = {
  kernelName: TopK,
  backendName: 'webgl',
  kernelFunc: topK
};